import type { Illustration as Kind } from '../data/mock';

// Simple flat illustrations stand in for photos, so the prototype carries no stock images.
export function Illustration({ kind, className }: { kind: Kind; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 400 260"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label={labels[kind]}
    >
      {art[kind]}
    </svg>
  );
}

const labels: Record<Kind, string> = {
  harbour: 'Container terminal at the harbour',
  trucks: 'Electric truck at a charging point',
  trucks2: 'Two trucks at the depot',
  pallets: 'Pallets staged at gate 4',
  team: 'Team photo',
  chart: 'Bar chart',
};

const art: Record<Kind, React.ReactNode> = {
  harbour: (
    <>
      <rect width="400" height="260" fill="#DCEBFA" />
      <circle cx="320" cy="64" r="30" fill="#FFF3D6" />
      <rect y="170" width="400" height="90" fill="#3A78C2" />
      <path d="M0 176c40-8 80 8 120 0s80-8 120 0 80 8 160-2v12H0z" fill="#5C93D3" />
      <g>
        <rect x="40" y="128" width="54" height="34" fill="#F4A361" />
        <rect x="96" y="128" width="54" height="34" fill="#1F4E86" />
        <rect x="152" y="128" width="54" height="34" fill="#92CC6C" />
        <rect x="68" y="92" width="54" height="34" fill="#3376C6" />
        <rect x="124" y="92" width="54" height="34" fill="#EE6E59" />
        <path d="M24 164h200l-18 20H42z" fill="#24364F" />
      </g>
      <g stroke="#24364F" strokeWidth="6" fill="none" strokeLinecap="round">
        <path d="M270 170V60h80" />
        <path d="M270 76l80-16" />
        <path d="M336 60v44" />
      </g>
      <rect x="324" y="104" width="26" height="18" fill="#F4A361" />
    </>
  ),
  trucks: (
    <>
      <rect width="400" height="260" fill="#E8F3E1" />
      <rect y="196" width="400" height="64" fill="#B9C2CC" />
      <rect x="40" y="96" width="190" height="96" rx="8" fill="#FFFFFF" />
      <rect x="230" y="122" width="70" height="70" rx="8" fill="#1F4E86" />
      <rect x="246" y="134" width="40" height="26" rx="4" fill="#BFD8F2" />
      <path d="M70 140l20-24v18h14l-20 26v-20z" fill="#92CC6C" />
      <text x="114" y="152" fontFamily="sans-serif" fontSize="22" fontWeight="700" fill="#1F4E86">
        NORDWIND
      </text>
      <circle cx="90" cy="196" r="18" fill="#2B2E34" />
      <circle cx="260" cy="196" r="18" fill="#2B2E34" />
      <rect x="330" y="110" width="30" height="86" rx="6" fill="#4F9A2C" />
      <rect x="337" y="122" width="16" height="16" rx="3" fill="#DDF3CF" />
      <path d="M345 196c0-20-30-10-40-30" stroke="#2B2E34" strokeWidth="4" fill="none" />
    </>
  ),
  trucks2: (
    <>
      <rect width="400" height="260" fill="#F6EEE6" />
      <rect y="190" width="400" height="70" fill="#C9CDD3" />
      <g>
        <rect x="24" y="110" width="150" height="76" rx="6" fill="#FFFFFF" />
        <rect x="174" y="128" width="54" height="58" rx="6" fill="#1F4E86" />
        <circle cx="64" cy="190" r="14" fill="#2B2E34" />
        <circle cx="196" cy="190" r="14" fill="#2B2E34" />
      </g>
      <g>
        <rect x="216" y="92" width="130" height="92" rx="6" fill="#F4A361" />
        <rect x="346" y="116" width="44" height="68" rx="6" fill="#1F4E86" />
        <circle cx="250" cy="190" r="14" fill="#2B2E34" />
        <circle cx="366" cy="190" r="14" fill="#2B2E34" />
      </g>
    </>
  ),
  pallets: (
    <>
      <rect width="400" height="260" fill="#EEF0F2" />
      <rect y="200" width="400" height="60" fill="#B7BCC3" />
      <rect x="20" y="30" width="360" height="12" fill="#C5C8CD" />
      <text x="300" y="80" fontFamily="sans-serif" fontSize="40" fontWeight="700" fill="#F4A361">
        4
      </text>
      {[40, 130, 220].map((x) => (
        <g key={x}>
          <rect x={x} y="140" width="80" height="56" fill="#D8B38A" />
          <rect x={x} y="110" width="80" height="30" fill="#E9CFAF" />
          <rect x={x} y="196" width="80" height="8" fill="#8A6A4A" />
          <path d={`M${x} 125h80M${x} 168h80`} stroke="#B98F64" strokeWidth="3" />
        </g>
      ))}
    </>
  ),
  team: (
    <>
      <rect width="400" height="260" fill="#FFF6EE" />
      {[
        [90, '#F4A361'],
        [160, '#3376C6'],
        [230, '#92CC6C'],
        [300, '#EE6E59'],
      ].map(([x, c]) => (
        <g key={String(x)}>
          <circle cx={Number(x)} cy="110" r="26" fill={String(c)} />
          <rect
            x={Number(x) - 34}
            y="142"
            width="68"
            height="90"
            rx="30"
            fill={String(c)}
            opacity=".7"
          />
        </g>
      ))}
    </>
  ),
  chart: (
    <>
      <rect width="400" height="260" fill="#F1EEFC" />
      <rect x="60" y="150" width="44" height="70" rx="6" fill="#9C85EC" />
      <rect x="130" y="110" width="44" height="110" rx="6" fill="#7B61C9" />
      <rect x="200" y="80" width="44" height="140" rx="6" fill="#5E45A8" />
      <rect x="270" y="50" width="44" height="170" rx="6" fill="#3B2A75" />
      <path d="M50 222h300" stroke="#5E45A8" strokeWidth="3" />
      <circle cx="330" cy="60" r="20" fill="#FFFFFF" />
      <path
        d="M321 60l7 7 12-14"
        stroke="#4F9A2C"
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
      />
    </>
  ),
};
